import React, { useState, useEffect } from "react";
import { Box, Typography, Modal, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import axios from "axios";
import AIMenuWrapper from "../../components/scene-ai/aimenuwrapper";

/**
 * NOTE:
 * - We fetch menu data from the MealViewer_MenuSetup API using the 'apiid' query param.
 * - If the 'url' param is also provided, we use that as the iframe URL.
 * - Otherwise, we fall back to 'api_link_base' or 'MenuUrl' from the API response.
 * - We must ensure that only the school name appears in the final URL path,
 *   then append `?start=` and `?end=` with the current month�s date range.
 */
const AImenu = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // --- Read query params ---
    const urlParams = new URLSearchParams(window.location.search);
    const paramApiid = urlParams.get("apiid") || "";
    const paramUrl = urlParams.get("url") || "";

    // --- Local State ---
    const [menuData, setMenuData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // --- Fetch Menu Data ---
    useEffect(() => {
        const fetchMenuData = async () => {
            if (!paramApiid) {
                setError("API ID is required to fetch menu data.");
                return;
            }
            setLoading(true);
            setError("");
            try {
                const response = await axios.get(
                    "https://api.swancreate.com/api/llm/ocps_MealViewer_MenuSetup",
                    { params: { apiid: paramApiid } }
                );
                setMenuData(response.data);
            } catch (err) {
                console.error("Error fetching menu data:", err);
                setError("Unable to load menu context data.");
            } finally {
                setLoading(false);
            }
        };
        fetchMenuData();
    }, [paramApiid]);

    // --- Helper to format YYYY-MM-DD ---
    const formatDate = (date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    // --- Determine the current month�s start/end ---
    const currentDate = new Date();
    const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
    );
    const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
    );

    const startParam = formatDate(startOfMonth);
    const endParam = formatDate(endOfMonth);

    // --- Determine iframe URL ---
    const rawIframeUrl =
        paramUrl || menuData?.api_link_base || menuData?.MenuUrl || "";

    // strip off any existing "?" and append ?start= & ?end= 
    const basePath = rawIframeUrl.replace(/\?.*$/, "");
    const iframeUrl = `${basePath}?start=${startParam}&end=${endParam}`;

    return (
        <Box
            sx={{
                // Fill the entire viewport
                width: "100vw",
                height: "100vh",
                display: "flex",
                flexDirection: "row",
                backgroundColor: colors.primary[900],
                overflow: "hidden", // no scroll on parent
            }}
        >
            {/* Left side: Iframe filling all available space */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    border: "none",
                    minWidth: 0, // prevents flex items from overflowing
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography
                        sx={{
                            margin: "auto",
                            textAlign: "center",
                            color: "#ffcccc",
                        }}
                    >
                        {error}
                    </Typography>
                ) : (
                    <iframe
                        src={iframeUrl}
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            flex: 1,
                        }}
                        title={menuData?.MealviewerSchoolName || "Menu Iframe"}
                    />
                )}
            </Box>

            {/* Right side: AI Chat Panel (fixed width) */}
            <Box
                sx={{
                    width: 350,
                    flexShrink: 0, // ensures it stays at 350px
                    borderLeft: "1px solid #CCC",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#E3F2FD",
                }}
            >
                <AIMenuWrapper
                    apiid={paramApiid}
                    menuData={menuData}
                    fallbackUrl={iframeUrl}
                />
            </Box>

            {/* Loading Overlay */}
            <Modal open={loading} sx={{ zIndex: 9999 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                >
                    <CircularProgress sx={{ color: "#FFFFFF" }} />
                </Box>
            </Modal>
        </Box>
    );
};

export default AImenu;
